import { useState, useEffect, useRef, useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai'; // Import useAtomValue
import { atom } from 'jotai';
import { v4 as uuidv4 } from 'uuid';
import { DataStore } from '@aws-amplify/datastore'; // Remove ZenObservable import
import { UserBoard } from '../models';
import { defaultPresetsData, PresetStub } from '../Components/Invigilation/Presets/defaultPresets.ts';
import { userIdAtom } from '../Jotai/Atoms/userIdAtom'; // Import userIdAtom

// Define the shape of a preset setting
interface PresetSetting {
    contrast: boolean;
    showSettingsPanel: boolean;
    arrangements: boolean;
    startZoomFactor: number;
    zoom: number;
    detailZoom: number;
    clockZoom: number;
    startClockZoom: number;
    titleZoom: number;
    subheaderZoom: number;
    timeZoom: number;
    chipZoom: number;
    reminderZoom: number;
    notesZoom: number;
    supervisionZoom: number;
    showNotes: boolean;
    showSupervision: boolean;
    viewMode: 'columns' | 'rows' | 'grid';
    numColumns: number;
    themeMode: 'light' | 'dark';
    // New time display settings
    showStartTime: boolean;
    showCurrentTime: boolean;
    showCountdown: boolean;
    showEndings: boolean; // Renamed from showEndTime
    showSeconds: boolean; // For current time clock
    // Added from InvigilationOptions.js
    use24HourTime: boolean;
    showQualification: boolean;
    showLevel: boolean;
    showFiveMins: boolean;
    showThirtyMins: boolean;
    useSound: boolean;
    showDividers: boolean;
    showGranularZoomControls: boolean;
    showPauseControls: boolean;
    showExtraTimeControls: boolean;
    // Added for Paper/Duration
    showPaper: boolean;
    showDuration: boolean;
}

// Define the shape of a preset object
interface Preset {
    id: string;
    name: string;
    settings: Partial<PresetSetting>;
    isDefault: boolean; // Make flag non-optional, clearly indicating origin
}

const PRESET_MARKER = "PRESET_V1";
// Atom now holds the combined list (default + user)
const presetsAtom = atom<Preset[]>([]);

export const usePresets = () => {
    const [presets, setPresets] = useAtom(presetsAtom);
    const owner = useAtomValue(userIdAtom); // Get the owner ID
    const [isLoading, setIsLoading] = useState(false); // Single loading state
    const [error, setError] = useState<string | null>(null);
    const operationInProgressRef = useRef(false); // Ref to prevent concurrent operations

    // Helper to parse settings safely
    const parseSettings = (qualifications: string | null | undefined, presetId: string): Partial<PresetSetting> => {
        if (typeof qualifications === 'object' && qualifications !== null) {
            return qualifications as Partial<PresetSetting>;
        } else if (typeof qualifications === 'string') {
            try {
                return JSON.parse(qualifications);
            } catch (e) {
                console.error(`Error parsing settings JSON for preset ${presetId}:`, e, "Raw value:", qualifications);
                return {};
            }
        } else {
            if (qualifications != null) {
                console.warn(`Preset ${presetId} qualifications field is unexpected type:`, typeof qualifications, qualifications);
            }
            return {};
        }
    };

    // Internal fetch logic for USER presets from DataStore ONLY
    const fetchUserPresetsFromDataStore = async (): Promise<Preset[]> => {
        // console.log("Fetching user presets from DataStore...");
        const userBoards = await DataStore.query(UserBoard, ub => ub.boardId.eq(PRESET_MARKER));
        // console.log(`Found ${userBoards.length} user presets in DataStore.`);
        return userBoards.map(ub => ({
            id: ub.id,
            name: ub.name || 'Unnamed Preset',
            settings: parseSettings(ub.qualifications, ub.id),
            isDefault: false // Mark explicitly as NOT default
        }));
    };

    // Function to combine default and user presets
    const combinePresets = (userPresets: Preset[]): Preset[] => {
        // console.log("Combining default and user presets...");
        const defaultPresetsMapped: Preset[] = defaultPresetsData.map((dp, index) => ({
            id: `default-${index}-${dp.name.replace(/\s+/g, '-')}`, // Generate a stable pseudo-ID for defaults
            name: dp.name,
            settings: dp.settings,
            isDefault: true // Mark explicitly as default
        }));

        // Filter out any user presets that might somehow have the same name as a default preset
        // This prevents duplicates if a user manually created one with a default name before this logic
        const defaultNames = new Set(defaultPresetsData.map(dp => dp.name));
        const filteredUserPresets = userPresets.filter(up => !defaultNames.has(up.name));

        const combined = [...defaultPresetsMapped, ...filteredUserPresets];
        // console.log(`Combined list has ${combined.length} presets.`);
        return combined;
    };

    // Wrapper for non-initial operations that should set loading state and prevent concurrency
    const runOperation = useCallback(async <T,>(opName: string, asyncFn: () => Promise<T>): Promise<T | null> => {
        if (operationInProgressRef.current) {
            console.warn(`Operation (${opName}) skipped: Another operation already in progress.`);
            return null;
        }
        operationInProgressRef.current = true;
        setIsLoading(true);
        setError(null);
        try {
            return await asyncFn();
        } catch (err: any) {
            console.error(`Error during operation (${opName}):`, err);
            setError(err.message || `An unexpected error occurred during ${opName}.`);
            return null;
        } finally {
            setIsLoading(false);
            operationInProgressRef.current = false;
        }
    }, []); // Empty dependency array, relies on closure

    // Exposed fetch function using the wrapper (for subscription calls and manual refresh)
    const fetchAndCombinePresets = useCallback(async () => {
        // console.log("fetchAndCombinePresets called...");
        // Use runOperation to handle loading state and concurrency
        const result = await runOperation('fetchAndCombinePresets', async () => {
            const userPresets = await fetchUserPresetsFromDataStore();
            const combinedData = combinePresets(userPresets);
            setPresets(combinedData); // Update atom with combined list
            // console.log("Presets atom updated with combined list.");
            return { success: true, data: combinedData };
        });
        return result ?? { success: false, message: "Fetch/Combine skipped or failed." };
    }, [runOperation, setPresets]); // Include setPresets

    // Exposed function to add a new preset (wrapped)
    const addPreset = async (name: string, settings: Partial<PresetSetting>) => {
        return await runOperation('addPreset', async () => {
            const presetName = name || 'New Preset';
            const presetSettings = settings || {};
            const settingsString = JSON.stringify(presetSettings);
            const newPresetRecord = await DataStore.save(
                new UserBoard({
                    name: presetName,
                    boardId: PRESET_MARKER,
                    qualifications: settingsString,
                    owner: owner, // Use the actual owner ID
                })
            );
            // console.log('Preset added to DataStore:', newPresetRecord);
            // Fetch and recombine after adding to update the list correctly
            await fetchAndCombinePresets();
            // Find the added preset in the *updated* combined list to return it
            const updatedPresets = presetsAtom.init; // Access the latest value directly (Jotai pattern)
            const addedPreset = updatedPresets.find(p => p.id === newPresetRecord.id);
            return { success: true, data: addedPreset, message: 'Preset added successfully.' };
        });
    };


    // Function to update an existing USER preset (wrapped)
    const updatePreset = async (presetId: string, updatedName?: string, updatedSettings?: Partial<PresetSetting>) => {
        // First, check if it's a default preset ID (these shouldn't be updated)
        const presetToUpdate = presets.find(p => p.id === presetId);
        if (presetToUpdate?.isDefault) {
            console.warn(`Attempted to update a default preset (${presetId}). Operation blocked.`);
            setError("Default presets cannot be modified. Please duplicate it first.");
            return { success: false, message: 'Default presets cannot be modified.' };
        }

        return await runOperation('updatePreset', async () => {
            // console.log(`Updating user preset ${presetId} in DataStore...`);
            const original = await DataStore.query(UserBoard, presetId);
            if (!original || original.boardId !== PRESET_MARKER) {
                // This case should be less likely now due to the isDefault check, but keep for safety
                throw new Error('User preset not found in DataStore or invalid.');
            }

            // Prevent updating a user preset to have the name of a default preset
            if (updatedName !== undefined && defaultPresetsData.some(dp => dp.name === updatedName)) {
                throw new Error(`Cannot rename preset to "${updatedName}" as it's a reserved default preset name.`);
            }

            await DataStore.save(
                UserBoard.copyOf(original, updated => {
                    if (updatedName !== undefined) updated.name = updatedName; // Already checked name collision
                    // Ensure settings are always stringified, even if empty
                    updated.qualifications = JSON.stringify(updatedSettings || {});
                })
            );
            // console.log('User preset updated in DataStore:', presetId);
            // Fetch and recombine after update
            await fetchAndCombinePresets();
            return { success: true, message: 'Preset updated successfully.' };
        });
    };


    // Function to delete a USER preset (wrapped)
    const deletePreset = async (presetId: string) => {
        // Prevent deleting default presets (double check)
        const presetToDelete = presets.find(p => p.id === presetId);
        if (presetToDelete?.isDefault) {
            console.warn(`Attempted to delete a default preset (${presetId}). Operation blocked.`);
            setError("Default presets cannot be deleted.");
            return { success: false, message: 'Default presets cannot be deleted.' };
        }

        // console.log(`[deletePreset] Attempting to delete USER preset with ID: ${presetId}`);
        return await runOperation('deletePreset', async () => {
            const toDelete = await DataStore.query(UserBoard, presetId);
            // console.log(`[deletePreset] Result of DataStore.query for ID ${presetId}:`, toDelete);
            if (!toDelete) {
                console.error(`[deletePreset] User preset with ID ${presetId} not found in DataStore.`);
                throw new Error(`User preset not found (ID: ${presetId}).`);
            }
            if (toDelete.boardId !== PRESET_MARKER) {
                console.error(`[deletePreset] Record found for ID ${presetId}, but it's not a preset (boardId: ${toDelete.boardId}).`);
                throw new Error(`Record found (ID: ${presetId}) is not a valid preset.`);
            }
            await DataStore.delete(toDelete);
            // console.log('[deletePreset] User preset deleted from DataStore:', presetId);
            // Fetch and recombine after delete
            await fetchAndCombinePresets();
            return { success: true, message: 'Preset deleted successfully.' };
        });
    };


    // Effect to fetch presets initially and set up subscription
    useEffect(() => {
        let isMounted = true;
        let subscription: any = null; // Variable to hold the subscription, typed as any

        const initialize = async () => {
            if (!isMounted || operationInProgressRef.current) return;
            operationInProgressRef.current = true;
            // console.log("[Effect] Initializing presets...");
            setIsLoading(true);
            setError(null);

            try {
                // Initial fetch and combine
                // console.log("[Effect] Performing initial fetch and combine...");
                const userPresets = await fetchUserPresetsFromDataStore();
                if (!isMounted) return; // Check mount status after async fetch
                const combinedData = combinePresets(userPresets);
                if (isMounted) setPresets(combinedData); // Set initial state
                // console.log("[Effect] Initial presets set.");

                // Set up subscription *after* initial load and state set
                if (isMounted) {
                    // console.log("[Effect] Setting up DataStore subscription...");
                    subscription = DataStore.observe(UserBoard, ub => ub.boardId.eq(PRESET_MARKER)).subscribe(msg => {
                        // console.log('DataStore UserBoard (Preset) subscription event:', msg.opType, msg.element.name);
                        if (isMounted) {
                            // Re-fetch and combine whenever DataStore changes for user presets
                            // console.log("[Subscription] Triggering fetchAndCombinePresets...");
                            fetchAndCombinePresets();
                        } else {
                            // console.log("[Subscription] Received event but component unmounted.");
                        }
                    });
                    // console.log("[Effect] Subscription active.");
                }

            } catch (initError: any) {
                console.error("[Effect] Error during initialization:", initError);
                if (isMounted) setError(initError.message || "Failed to initialize presets.");
            } finally {
                // console.log("[Effect] Initialization finished.");
                if (isMounted) setIsLoading(false);
                operationInProgressRef.current = false; // Release lock
            }
        };

        initialize(); // Call the async function

        // Synchronous cleanup function returned by useEffect
        return () => {
            // console.log("[Effect] Cleanup running...");
            isMounted = false;
            if (subscription) {
                // console.log("[Effect] Unsubscribing...");
                subscription.unsubscribe(); // Unsubscribe if subscription was created
                // console.log("[Effect] Unsubscribed.");
            } else {
                // console.log("[Effect] No active subscription to unsubscribe from.");
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchAndCombinePresets]); // fetchAndCombinePresets is stable due to useCallback


    // Function to apply a preset's settings (conceptual, applied in component)
    const applyPreset = (presetId: string) => {
        const presetToApply = presets.find(p => p.id === presetId);
        if (presetToApply) {
            // console.log('Applying preset (from hook - implementation needed in component):', presetToApply.name, presetToApply.settings);
            // Example (needs actual setters from relevant hooks/atoms):
            // setPreferences(prev => ({ ...prev, ...presetToApply.settings }));
            // setViewMode(presetToApply.settings.viewMode);
            // setNumColumns(presetToApply.settings.numColumns);
            // setThemeMode(presetToApply.settings.themeMode); // If theme is part of preset
            // ... etc.
        } else {
            console.warn('Preset not found:', presetId);
        }
    };


    return {
        presets,
        isLoading, // Use the single loading state
        error,
        fetchPresets: fetchAndCombinePresets, // Expose the combined fetcher
        addPreset,
        updatePreset, // Now correctly handles default presets
        deletePreset,
        applyPreset,
    };
}; // This is the correct closing brace for usePresets
